import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import db from "../../utils/firebase.config";
import {
  createWardDraft,
  ensureUniqueSlug,
  formatInvitationLabel,
  getFirestoreErrorMessage,
  getSlugSeedFromGuest,
  normalizeWards,
  slugify,
} from "./helpers";

const mapGuestFromDoc = (guestDoc) => {
  const data = guestDoc.data();
  return {
    id: guestDoc.id,
    localId: guestDoc.id,
    name: data.name || "",
    slug: data.slug || "",
    mobile: data.mobile || "",
    table: data.table || "",
    confirmed: data.confirmed === true,
    wards: Array.isArray(data.wards)
      ? data.wards.map((ward) => ({
          id: ward.id || null,
          name: ward.name || "",
          table: ward.table || "",
          confirmed: ward.confirmed === true,
        }))
      : [],
  };
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const ManagePage = () => {
  const [invitations, setInvitations] = useState([]);
  const [selectedInvitationId, setSelectedInvitationId] = useState("");
  const [familyAlias, setFamilyAlias] = useState("");
  const [guests, setGuests] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [invitationSearch, setInvitationSearch] = useState("");
  const [guestSearch, setGuestSearch] = useState("");

  const selectedInvitation = useMemo(
    () => invitations.find((invitation) => invitation.id === selectedInvitationId),
    [invitations, selectedInvitationId]
  );

  const filteredInvitationOptions = useMemo(() => {
    const term = normalizeText(invitationSearch);
    if (!term) {
      return invitations;
    }

    return invitations.filter((invitation) => {
      const searchable = normalizeText(invitation.searchIndex);
      return searchable.includes(term);
    });
  }, [invitations, invitationSearch]);

  const filteredGuests = useMemo(() => {
    const term = normalizeText(guestSearch);
    if (!term) {
      return guests;
    }

    return guests.filter((guest) => {
      const candidate = [
        guest.name,
        guest.mobile,
        guest.slug,
        guest.table,
        selectedInvitation?.familyAlias,
        ...(guest.wards || []).flatMap((ward) => [
          ward.name,
          ward.table,
          ward.id,
          ward.confirmed ? "confirmado" : "no confirmado",
        ]),
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .trim();

      return normalizeText(candidate).includes(term);
    });
  }, [guestSearch, guests, selectedInvitation]);

  const loadInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    setError("");
    try {
      const snapshot = await getDocs(collection(db, "invitation"));
      const nextInvitations = await Promise.all(
        snapshot.docs.map(async (invitationDoc) => {
          const invitationData = invitationDoc.data() || {};
          const invitationId = invitationDoc.id;
          const guestsSnap = await getDocs(collection(db, `invitation/${invitationId}/guess`));

          const guestSearchBlob = guestsSnap.docs
            .map((guestDoc) => {
              const guestData = guestDoc.data() || {};
              const wardsBlob = Array.isArray(guestData.wards)
                ? guestData.wards.map((ward) => `${ward?.name || ""} ${ward?.table || ""}`).join(" ")
                : "";
              return `${guestData.name || ""} ${guestData.mobile || ""} ${guestData.slug || ""} ${guestData.table || ""} ${wardsBlob}`;
            })
            .join(" ");

          return {
            id: invitationId,
            ...invitationData,
            searchIndex: `${invitationData.familyAlias || ""} ${invitationId} ${guestSearchBlob}`,
          };
        })
      );

      const sortedInvitations = nextInvitations
        .sort((a, b) =>
          (a.familyAlias || "").localeCompare(b.familyAlias || "")
        );

      setInvitations(sortedInvitations);

      if (
        selectedInvitationId &&
        !sortedInvitations.some((invitation) => invitation.id === selectedInvitationId)
      ) {
        setSelectedInvitationId("");
      }
    } catch (loadError) {
      console.error("load invitations error", loadError);
      setError(getFirestoreErrorMessage(loadError));
    } finally {
      setLoadingInvitations(false);
    }
  }, [selectedInvitationId]);

  const loadInvitationDetails = useCallback(async (invitationId) => {
    if (!invitationId) {
      setFamilyAlias("");
      setGuests([]);
      return;
    }

    setLoadingDetails(true);
    setError("");
    try {
      const invitationRef = doc(db, "invitation", invitationId);
      const invitationSnap = await getDoc(invitationRef);
      if (!invitationSnap.exists()) {
        setError("La invitación ya no existe");
        setGuests([]);
        setFamilyAlias("");
        return;
      }

      setFamilyAlias(invitationSnap.data().familyAlias || "");

      const guestsSnap = await getDocs(collection(db, `invitation/${invitationId}/guess`));
      setGuests(guestsSnap.docs.map(mapGuestFromDoc));
    } catch (detailsError) {
      console.error("load invitation details error", detailsError);
      setError(getFirestoreErrorMessage(detailsError));
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    loadInvitations().then(() => {});
  }, [loadInvitations]);

  useEffect(() => {
    loadInvitationDetails(selectedInvitationId).then(() => {});
  }, [loadInvitationDetails, selectedInvitationId]);

  const updateGuestField = (localId, field, value) => {
    setGuests((current) =>
      current.map((guest) => {
        if (guest.localId !== localId) {
          return guest;
        }

        const updatedGuest = { ...guest, [field]: value };
        if (field === "name" && !updatedGuest.slug) {
          updatedGuest.slug = slugify(value);
        }
        return updatedGuest;
      })
    );
  };

  const updateWardField = (guestLocalId, wardIndex, field, value) => {
    setGuests((current) =>
      current.map((guest) => {
        if (guest.localId !== guestLocalId) {
          return guest;
        }

        const nextWards = [...guest.wards];
        nextWards[wardIndex] = { ...nextWards[wardIndex], [field]: value };
        return { ...guest, wards: nextWards };
      })
    );
  };

  const removeWard = (guestLocalId, wardIndex) => {
    setGuests((current) =>
      current.map((guest) => {
        if (guest.localId !== guestLocalId) {
          return guest;
        }

        return {
          ...guest,
          wards: guest.wards.filter((_, idx) => idx !== wardIndex),
        };
      })
    );
  };

  const addWard = (guestLocalId) => {
    setGuests((current) =>
      current.map((guest) => {
        if (guest.localId !== guestLocalId) {
          return guest;
        }
        return {
          ...guest,
          wards: [...guest.wards, createWardDraft()],
        };
      })
    );
  };

  const validateGuest = (guest) => {
    if (!guest.name.trim()) {
      return "El nombre del invitado es obligatorio";
    }
    if (!guest.mobile.trim() || !/^\d{10}$/.test(guest.mobile.trim())) {
      return "El teléfono del invitado debe tener 10 dígitos";
    }

    for (const [wardIndex, ward] of guest.wards.entries()) {
      if (!ward.name.trim()) {
        return `Falta nombre del acompañante #${wardIndex + 1}`;
      }
    }

    return "";
  };

  const saveGuest = async (guestLocalId) => {
    if (!selectedInvitationId) {
      return;
    }

    const guest = guests.find((item) => item.localId === guestLocalId);
    if (!guest) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const validationError = validateGuest(guest);
      if (validationError) {
        setError(validationError);
        return;
      }

      const usedSlugs = new Set(
        guests
          .filter((item) => item.localId !== guestLocalId)
          .map((item) => item.slug)
          .filter(Boolean)
      );
      const nextSlug = ensureUniqueSlug(getSlugSeedFromGuest(guest), usedSlugs);
      const duplicatedMobileQuery = query(
        collectionGroup(db, "guess"),
        where("mobile", "==", guest.mobile.trim())
      );
      const duplicatedMobileSnapshot = await getDocs(duplicatedMobileQuery);
      const duplicateInAnotherGuest = duplicatedMobileSnapshot.docs.find((docSnap) => {
        const invitationId = docSnap.ref.parent.parent?.id;
        const isSameGuest =
          !!guest.id &&
          invitationId === selectedInvitationId &&
          docSnap.id === guest.id;
        return !isSameGuest;
      });

      if (duplicateInAnotherGuest) {
        setError("El número de teléfono ya existe en otra invitación");
        return;
      }

      const now = new Date().toISOString();

      const payload = {
        name: guest.name.trim(),
        slug: nextSlug,
        mobile: guest.mobile.trim(),
        table: (guest.table || "").trim(),
        confirmed: guest.confirmed === true,
        wards: normalizeWards(guest.wards),
        updatedAt: now,
        updateAt: now,
      };

      if (guest.id) {
        await updateDoc(
          doc(db, `invitation/${selectedInvitationId}/guess`, guest.id),
          payload
        );
      } else {
        const created = await addDoc(
          collection(db, `invitation/${selectedInvitationId}/guess`),
          {
            ...payload,
            createdAt: now,
          }
        );
        payload.id = created.id;
      }

      setSuccess("Invitado guardado correctamente");
      await loadInvitationDetails(selectedInvitationId);
    } catch (saveError) {
      console.error("save guest error", saveError);
      setError(getFirestoreErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const deleteGuest = async (guestLocalId) => {
    if (!selectedInvitationId) {
      return;
    }

    const guest = guests.find((item) => item.localId === guestLocalId);
    if (!guest) {
      return;
    }

    const confirmed = window.confirm(`¿Eliminar a ${guest.name || "este invitado"}?`);
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (guest.id) {
        await deleteDoc(doc(db, `invitation/${selectedInvitationId}/guess`, guest.id));
      }

      setGuests((current) =>
        current.filter((item) => item.localId !== guestLocalId)
      );
      setSuccess("Invitado eliminado");
    } catch (deleteError) {
      console.error("delete guest error", deleteError);
      setError(getFirestoreErrorMessage(deleteError));
    } finally {
      setSaving(false);
    }
  };

  const saveFamilyAlias = async () => {
    if (!selectedInvitationId) {
      return;
    }
    if (!familyAlias.trim()) {
      setError("El alias de familia no puede quedar vacío");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await updateDoc(doc(db, "invitation", selectedInvitationId), {
        familyAlias: familyAlias.trim(),
        updatedAt: new Date().toISOString(),
      });
      setSuccess("Alias de familia actualizado");
      await loadInvitations();
    } catch (saveError) {
      console.error("save family alias error", saveError);
      setError(getFirestoreErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const deleteInvitation = async () => {
    if (!selectedInvitationId) {
      return;
    }

    const confirmed = window.confirm(
      `¿Eliminar la invitación ${selectedInvitation?.familyAlias || "seleccionada"} y todos sus invitados?`
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const guestsRef = collection(db, `invitation/${selectedInvitationId}/guess`);
      const guestsSnap = await getDocs(guestsRef);

      const batch = writeBatch(db);
      guestsSnap.forEach((guestDoc) => {
        batch.delete(guestDoc.ref);
      });
      batch.delete(doc(db, "invitation", selectedInvitationId));
      await batch.commit();

      setSuccess("Invitación eliminada correctamente");
      setSelectedInvitationId("");
      setFamilyAlias("");
      setGuests([]);
      await loadInvitations();
    } catch (deleteError) {
      console.error("delete invitation error", deleteError);
      setError(getFirestoreErrorMessage(deleteError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-section">
        <h3>Gestionar Invitaciones</h3>
        <p>Edita y elimina familias, invitados y acompañantes en Firestore.</p>
      </div>

      <div className="admin-form">
        <div className="manage-toolbar">
          <div className="field-block manage-select">
            <label htmlFor="invitationSearch">Buscar invitación</label>
            <input
              id="invitationSearch"
              type="text"
              value={invitationSearch}
              onChange={(e) => setInvitationSearch(e.target.value)}
              placeholder="Alias, ID, nombre de invitado o acompañante"
            />
            <p className="manage-hint">
              Coincidencias: {filteredInvitationOptions.length}
            </p>
            <label htmlFor="invitationSelect">Invitación</label>
            <select
              id="invitationSelect"
              value={selectedInvitationId}
              onChange={(e) => setSelectedInvitationId(e.target.value)}
            >
              <option value="">Selecciona una invitación</option>
              {filteredInvitationOptions.map((invitation) => (
                <option key={invitation.id} value={invitation.id}>
                  {formatInvitationLabel(invitation)}
                </option>
              ))}
            </select>
            {invitationSearch.trim() && filteredInvitationOptions.length > 0 && (
              <div className="quick-results">
                {filteredInvitationOptions.slice(0, 6).map((invitation) => (
                  <button
                    key={`quick-${invitation.id}`}
                    type="button"
                    className="quick-result-btn"
                    onClick={() => setSelectedInvitationId(invitation.id)}
                  >
                    {formatInvitationLabel(invitation)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="manage-actions-top">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => loadInvitations()}
              disabled={loadingInvitations}
            >
              {loadingInvitations ? "Actualizando..." : "Actualizar lista"}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={deleteInvitation}
              disabled={!selectedInvitationId || saving}
            >
              Eliminar invitación
            </button>
          </div>
        </div>

        {selectedInvitationId && (
          <div className="family-alias-edit">
            <div className="field-block">
              <label htmlFor="familyAliasEdit">Alias de familia</label>
              <input
                id="familyAliasEdit"
                type="text"
                value={familyAlias}
                onChange={(e) => setFamilyAlias(e.target.value)}
                placeholder="Alias de familia"
              />
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={saveFamilyAlias}
              disabled={saving}
            >
              Guardar alias
            </button>
          </div>
        )}

        {loadingDetails && <p className="status">Cargando detalles...</p>}

        {selectedInvitationId && !loadingDetails && (
          <div className="guests-section">
            <div className="field-block">
              <label htmlFor="guestSearch">Buscar invitado</label>
              <input
                id="guestSearch"
                type="text"
                value={guestSearch}
                onChange={(e) => setGuestSearch(e.target.value)}
                placeholder="Nombre, teléfono, slug, mesa o acompañante"
              />
              <p className="status">
                Mostrando {filteredGuests.length} de {guests.length} invitados
              </p>
            </div>

            {filteredGuests.map((guest, guestIndex) => (
              <div className="guest-card" key={guest.localId}>
                <div className="guest-card-header">
                  <h4>
                    Invitado #{guestIndex + 1} {guest.id ? `(ID: ${guest.id})` : "(nuevo)"}
                  </h4>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => deleteGuest(guest.localId)}
                    disabled={saving}
                  >
                    Eliminar invitado
                  </button>
                </div>

                <div className="guest-grid">
                  <input
                    type="text"
                    value={guest.name}
                    onChange={(e) =>
                      updateGuestField(guest.localId, "name", e.target.value)
                    }
                    placeholder="Nombre"
                  />
                  <input
                    type="text"
                    value={guest.slug}
                    onChange={(e) =>
                      updateGuestField(
                        guest.localId,
                        "slug",
                        slugify(e.target.value)
                      )
                    }
                    placeholder="Slug"
                  />
                  <input
                    type="text"
                    value={guest.mobile}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) =>
                      updateGuestField(
                        guest.localId,
                        "mobile",
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    placeholder="Teléfono (10 dígitos)"
                  />
                  <input
                    type="text"
                    value={guest.table}
                    onChange={(e) =>
                      updateGuestField(guest.localId, "table", e.target.value)
                    }
                    placeholder="Mesa (opcional)"
                  />
                </div>

                <label className="checkbox-line">
                  <input
                    type="checkbox"
                    checked={guest.confirmed}
                    onChange={(e) =>
                      updateGuestField(
                        guest.localId,
                        "confirmed",
                        e.target.checked
                      )
                    }
                  />
                  Confirmado
                </label>

                <div className="wards-section">
                  <div className="wards-header">
                    <h5>Acompañantes</h5>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => addWard(guest.localId)}
                    >
                      Agregar acompañante
                    </button>
                  </div>

                  {guest.wards.length === 0 && (
                    <p className="ward-empty">Sin acompañantes</p>
                  )}

                  {guest.wards.map((ward, wardIndex) => (
                    <div className="ward-row" key={`${guest.localId}-${wardIndex}`}>
                      <input
                        type="text"
                        value={ward.name}
                        onChange={(e) =>
                          updateWardField(
                            guest.localId,
                            wardIndex,
                            "name",
                            e.target.value
                          )
                        }
                        placeholder="Nombre acompañante"
                      />
                      <input
                        type="text"
                        value={ward.table}
                        onChange={(e) =>
                          updateWardField(
                            guest.localId,
                            wardIndex,
                            "table",
                            e.target.value
                          )
                        }
                        placeholder="Mesa (opcional)"
                      />
                      <label className="checkbox-line">
                        <input
                          type="checkbox"
                          checked={ward.confirmed}
                          onChange={(e) =>
                            updateWardField(
                              guest.localId,
                              wardIndex,
                              "confirmed",
                              e.target.checked
                            )
                          }
                        />
                        Confirmado
                      </label>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => removeWard(guest.localId, wardIndex)}
                      >
                        Eliminar
                      </button>
                    </div>
                  ))}
                </div>

                <div className="action-row">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => saveGuest(guest.localId)}
                    disabled={saving}
                  >
                    Guardar invitado
                  </button>
                </div>
              </div>
            ))}

          </div>
        )}

        {error && <p className="status error">{error}</p>}
        {success && <p className="status success">{success}</p>}
      </div>
    </div>
  );
};

export default ManagePage;
